import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import Snackbar from "../../common/Snackbar";
import {
  createTrainingLog,
  updateTrainingLog,
} from "../../redux/trainingLogSlice";
import { buildPermissionContext } from "../../util/permissions";

async function generateSHA256(input) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const AddTrainingLog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  const isEditMode = location.state?.editMode || false;
  const existingLog = location.state?.log || {};

  const [logData, setLogData] = useState({
    procedure_revision_ref: "",
    date_of_training: "",
    trainer: { first_name: "", last_name: "" },
    trainer_signature: "",
    trainee: { first_name: "", last_name: "" },
    trainee_signature: "",
    revision_trained: "",
    training_status_of_trainee: "",
  });

  const [auditData, setAuditData] = useState({
    user_ref: localStorage.getItem("user_id") || "",
    ip_address: "",
    device_info: navigator.userAgent,
    hash: generateSHA256(Math.random().toString()),
    signature: "",
  });

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) =>
        setAuditData((prev) => ({ ...prev, ip_address: data.ip }))
      )
      .catch((err) => console.error("Failed to fetch IP:", err));
  }, []);

  useEffect(() => {
    if (isEditMode && existingLog) {
      setLogData(existingLog);
    }
  }, [isEditMode, existingLog]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLogData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTrainerChange = (e) => {
    const { name, value } = e.target;
    setLogData((prev) => ({
      ...prev,
      trainer: {
        ...prev.trainer,
        [name]: value,
      },
    }));
  };

  const handleTraineeChange = (e) => {
    const { name, value } = e.target;
    setLogData((prev) => ({
      ...prev,
      trainee: {
        ...prev.trainee,
        [name]: value,
      },
    }));
  };

  const handleSignatureChange = (e) => {
    setAuditData((prev) => ({
      ...prev,
      signature: e.target.value,
    }));
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !logData.date_of_training.trim() ||
      !logData.trainer.first_name.trim()
    ) {
      showSnackbar("Date of Training and Trainer are required!", "error");

      return;
    }
    const hash = await generateSHA256(Math.random().toString());

    const finalData = {
      ...logData,
      audit_trail_data: {
        ...auditData,
        hash,

        action: isEditMode ? "UPDATE_TRAINING_LOG" : "CREATE_TRAINING_LOG",
        comment: isEditMode ? `Updated Training Log` : `Created Training Log`,
        date: new Date().toISOString(),
        version_content: {},
      },
    };

    if (isEditMode) {
      dispatch(
        updateTrainingLog({ id: existingLog.training_log_id, data: finalData })
      );
    } else {
      dispatch(createTrainingLog(finalData));
    }

    navigate("/training-log");
  };

  return (
    <div>
      <AppBar
        title={isEditMode ? "Edit Training Log" : "Add Training Log"}
        buttons={[
          {
            label: "Save",
            onClick: canEdit ? handleSubmit : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save training logs."
              : undefined,
          },
        ]}
      />
      <form className="training-log-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Procedure Number:</label>
          <input
            type="text"
            name="procedure_revision_ref"
            value={logData.procedure_revision_ref}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Date of Training:</label>
          <input
            type="date"
            name="date_of_training"
            value={
              logData.date_of_training
                ? logData.date_of_training.split("T")[0]
                : ""
            }
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Trainer First Name:</label>
          <input
            type="text"
            name="first_name"
            value={logData.trainer.first_name}
            onChange={handleTrainerChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Trainer Last Name:</label>
          <input
            type="text"
            name="last_name"
            value={logData.trainer.last_name}
            onChange={handleTrainerChange}
          />
        </div>
        <div className="form-group">
          <label>Trainer Signature:</label>
          <textarea
            name="trainer_signature"
            value={logData.trainer_signature}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Trainee First Name:</label>
          <input
            type="text"
            name="first_name"
            value={logData.trainee.first_name}
            onChange={handleTraineeChange}
          />
        </div>
        <div className="form-group">
          <label>Trainee Last Name:</label>
          <input
            type="text"
            name="last_name"
            value={logData.trainee.last_name}
            onChange={handleTraineeChange}
          />
        </div>
        <div className="form-group">
          <label>Trainee Signature:</label>
          <textarea
            name="trainee_signature"
            value={logData.trainee_signature}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Revision Trained:</label>
          <input
            type="text"
            name="revision_trained"
            value={logData.revision_trained}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Training Status of Trainee:</label>
          <input
            type="text"
            name="training_status_of_trainee"
            value={logData.training_status_of_trainee}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Signature:</label>
          <textarea
            name="signature"
            value={auditData.signature}
            onChange={handleSignatureChange}
          />
        </div>
      </form>

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </div>
  );
};

export default AddTrainingLog;
